import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

export default function GPTChat() {
  const [prompt, setPrompt] = useState("");
  const [image, setImage] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "👋 Welcome to OrganicGPT! I can help you diagnose plant problems using text or image and recommend organic products. Describe your crop issue or upload a plant photo to get started.",
    },
  ]);
  const [products, setProducts] = useState([]);

  const formatMarkdownToHTML = (text) => {
    let html = text;

    html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/(\d+)\.\s(.*?)(?=\d+\.|\n|$)/gs, (_, num, item) => {
      const items = item.split("\n").map(i => i.trim()).filter(Boolean);
      return `<ol start="${num}">${items
        .map((i) => `<li>${i.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")}</li>`)
        .join("")}</ol>`;
    });

    html = html.replace(/[-•]\s+(.*?)(?=\n|$)/g, "<li>$1</li>");
    if (html.includes("<li>")) html = html.replace(/(<li>.*<\/li>)/gs, "<ul>$1</ul>");

    html = html.replace(/\n{2,}/g, "<br/><br/>").replace(/\n/g, "<br/>");

    html = html
      .split(/<br\/><br\/>/)
      .map((para) => `<p>${para.trim()}</p>`)
      .join("");

    return html;
  };

  const groupLinks = {
    Nutrition: "https://www.matihaat.com/nutrition",
    Protection: "https://www.matihaat.com/protection",
    Soils: "https://www.matihaat.com/soils",
    "Heritage Seeds": "https://www.matihaat.com/heritage-seeds",
    Equipment: "https://www.matihaat.com/equipment",
    Gardening: "https://www.matihaat.com/gardening",
  };

  const tagMap = {
    soil: "Soils",
    nutrient: "Nutrition",
    deficiency: "Nutrition",
    fertilizer: "Nutrition",
    fungus: "Protection",
    fungal: "Protection",
    bacteria: "Protection",
    insect: "Protection",
    pest: "Protection",
    infection: "Protection",
    seed: "Heritage Seeds",
    tool: "Equipment",
    spray: "Equipment",
    machine: "Equipment",
    garden: "Gardening",
    compost: "Gardening",
  };

  const extractRelevantTags = (text) => {
    const matchedTags = new Set();
    Object.entries(tagMap).forEach(([keyword, tag]) => {
      if (text.includes(keyword)) {
        matchedTags.add(tag);
      }
    });
    return Array.from(matchedTags);
  };

  const handleExcelUpload = async () => {
    const res = await fetch("/Mati All Products - 18 April 2025.xlsx");
    const data = await res.arrayBuffer();
    const workbook = XLSX.read(data, { type: "buffer" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(sheet);
    setProducts(json);
  };

  const handleFileChange = (e) => {
    setImage(e.target.files[0]);
  };

  const getBase64Image = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleSend = async () => {
    if (!prompt && !image) return;

    let imageContent = "";
    if (image) {
      imageContent = await getBase64Image(image);
    }

    const userMessage = { role: "user", content: prompt };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt("");
    setMessages((prev) => [...prev, { role: "assistant", content: "<em>Bot is typing...</em>" }]);

    const payload = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content:
            "You are OrganicGPT, an expert in plant health. Diagnose crop issues from text or image and recommend organic solutions. Use only product tags from: Nutrition, Protection, Soils, Heritage Seeds, Equipment, Gardening.",
        },
        {
          role: "user",
          content: imageContent
            ? [
                { type: "text", text: prompt },
                {
                  type: "image_url",
                  image_url: { url: imageContent },
                },
              ]
            : prompt,
        },
      ],
    };

    try {
     const response = await axios.post("https://backend-matihaat.onrender.com/ask", {
      messages: payload.messages,
    });

      const reply = response.data.choices[0].message.content;
      const lowerReply = reply.toLowerCase();

      const matchedTags = extractRelevantTags(lowerReply);
      const matchedProducts = products.filter((p) =>
        matchedTags.includes(p.Tags?.trim())
      );

      let formattedReply = formatMarkdownToHTML(reply);

      matchedTags.forEach((tag) => {
        const groupLink = groupLinks[tag] || "#";
        const relevantProducts = matchedProducts
          .filter((p) => p.Tags?.trim() === tag)
          .slice(0, 4);

        if (relevantProducts.length > 0) {
          formattedReply += `
            <div style="margin-top: 10px; border-top: 1px solid #eee; padding-top: 10px;">
              <strong>🌱 Recommended Products – ${tag}</strong>
              <ul>
                ${relevantProducts.map((p) => `<li>${p.Name}</li>`).join("")}
              </ul>
              <a href="${groupLink}" target="_blank" style="color: green;">🔗 View more ${tag} products</a>
            </div>
          `;
        }
      });

      setMessages((prev) => {
        const msgs = prev.filter(msg => msg.content !== "<em>Bot is typing...</em>");
        return [...msgs, { role: "assistant", content: formattedReply }];
      });
    } catch (err) {
      console.error("GPT Error:", err);
      setMessages((prev) => {
        const msgs = prev.filter(msg => msg.content !== "<em>Bot is typing...</em>");
        return [...msgs, { role: "assistant", content: "⚠️ Something went wrong. Please try again later." }];
      });
    }

    setImage(null);
  };

  useEffect(() => {
    handleExcelUpload();
  }, []);

  return (
    <div
      className="chat-container"
      style={{
        width: "70vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Arial, sans-serif",
        position: "absolute",
        marginLeft: "14vw",
        marginTop: "2vw"
      }}
    >
      <div
        className="chat-header"
        style={{ background: "#198754", color: "#fff", padding: "16px" }}
      >
        <h2>🌿 OrganicGPT – Plant Health Chat</h2>
      </div>

      <div
        className="chat-box"
        style={{
          background: "#f9f9f9",
          height: "600px",
          overflowY: "auto",
          padding: "16px",
        }}
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              textAlign: msg.role === "user" ? "right" : "left",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: "10px 14px",
                borderRadius: "10px",
                background: msg.role === "user" ? "#dcf8c6" : "#fff",
                maxWidth: "90%",
              }}
              dangerouslySetInnerHTML={{ __html: msg.content }}
            />
          </div>
        ))}
      </div>

      <div style={{ padding: "16px", borderTop: "1px solid #eee", display: "flex", flexDirection: "column", gap: "8px" }}>
        {/* Choose image above */}
        <div>
          <input
            id="file-upload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <label
            htmlFor="file-upload"
            style={{
              display: "inline-block",
              padding: "10px 20px",
              backgroundColor: "#198754",
              color: "#fff",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
              userSelect: "none",
            }}
          >
            {image ? `Selected: ${image.name}` : "📁 Choose Image"}
          </label>
        </div>

        {/* Text + send button inline */}
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            type="text"
            value={prompt}
            placeholder="Describe the issue or ask a question..."
            onChange={(e) => setPrompt(e.target.value)}
            style={{
              flexGrow: 1,
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              fontSize: "16px",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button
            onClick={handleSend}
            style={{
              padding: "10px 20px",
              background: "#198754",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
            aria-label="Send message"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
